import { StringResult } from '@microblink/blinkid-in-browser-sdk';

import { CountryCode } from 'lib/constants/locales';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { PreparedFileUpload } from 'lib/utils/dataUrl';
import { VerificationStatus } from 'lib/verification';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';

interface VerificationCoreAttributes {
  'country-code'?: string;
  status?: VerificationStatus;
  'created-at'?: string;
  'completed-at'?: string;
  'submitted-at'?: string;
}

interface VerificationDateAttributes {
  'issue-date'?: string;
  'expiration-date'?: string;
}

export enum CaptureMethod {
  Live = 'live',
  Auto = 'auto',
  Manual = 'manual',
  Upload = 'upload',
}

export enum CaptureFlow {
  /**
   * Image was captured from the webcam flow
   */
  Guided = 'guided',
  /**
   * Image was uploaded
   */
  Unguided = 'unguided',
}

export interface VerificationPreparedFileUpload {
  page: GovernmentIdSide;
  'capture-flow': CaptureFlow;
  'capture-method': CaptureMethod;
  data: PreparedFileUpload[];
}

interface SharedGovernmentIdAttributes {
  'entity-confidence-reasons'?: ConfidenceReasonIdVerify[];
  'capture-method'?: CaptureMethod;
}

export interface VerificationGovernmentIdAttributes
  extends VerificationCoreAttributes,
    VerificationDateAttributes,
    SharedGovernmentIdAttributes {
  'selected-id-class'?: string;
  'client-extraction-raws'?: ClientExtraction[];
}

export interface ClientExtraction {
  type: string;
  value: string | Record<string, unknown>;
}

export interface ClientExtractionBarcode extends ClientExtraction {
  type: 'pdf417';
  value: string;
}

export interface ClientExtractionCard extends ClientExtraction {
  type: 'card';
  value: {
    front?: QuadPoints;
    back?: QuadPoints;
    barcode?: QuadPoints;
  };
}

interface QuadPoint {
  x: number;
  y: number;
}

interface QuadPoints extends Record<string, unknown> {
  'top-left': QuadPoint;
  'top-right': QuadPoint;
  'bottom-left': QuadPoint;
  'bottom-right': QuadPoint;
}

export interface ClientExtractionViz extends ClientExtraction {
  type: 'viz';
  value: {
    'name-first'?: StringResult;
    'name-last'?: StringResult;
    'identification-number'?: StringResult;
    address?: StringResult;
    birthdate?: StringResult;
    'expiration-date'?: StringResult;
  };
}

export interface ClientExtractionClassification extends ClientExtraction {
  type: 'classification';
  value: {
    type?: string;
    country?: string;
    region?: string;
  };
}

/**
 * These are only the reasons shown to individuals and do NOT include all confidence reasons.
 * E.g., for validation checks associated with fraudulent behavior, we simply
 * return the ConfidenceReasonIdVerify.Generic enum to obscure our validation methodology
 * from potential abusers.
 */
export enum ConfidenceReasonIdVerify {
  PortraitMissing = 'portrait_missing',
  PortraitNotClear = 'portrait_not_clear',
  /**
   * FIXME: This is never sent by the backend. Unclear what this is meant to correspond to.
   */
  DisabledIdClass = 'disabled_id_class',
  MilitaryId = 'military_id',

  BarcodeNotDetected = 'barcode_not_detected',
  MrzNotDetected = 'mrz_not_detected',
  Expired = 'expired',

  FailedAgeComparison = 'failed_age_comparison',
  /**
   * Was changed from 'double_front_detected' to just 'double_front' at some point.
   */
  DoubleFrontDetected = 'double_front',
  Blurry = 'blurry',
  Glare = 'glare',

  CornerMissing = 'corner_missing',
  SelectedCountryMismatch = 'selected_country_mismatch',
  UnprocessableImage = 'unprocessable_image',

  // Client-side only for failure on score
  Generic = 'generic',
}

export interface VerificationEmailAddressAttributes extends VerificationCoreAttributes {
  'email-address'?: string;
  'confirmation-code'?: string;
}

export interface VerificationPhoneNumberAttributes extends VerificationCoreAttributes {
  'phone-number'?: string;
  'confirmation-code'?: string;
  'name-first'?: string;
  'name-last'?: string;
}

export enum VerificationDatabaseDisplayError {
  AddressDeliverableDetection = 'database_address_deliverable_detection',
}

export interface VerificationAamvaAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-last'?: string;
  birthdate?: string;
  'address-street-1'?: string;
  'address-street-2'?: string;
  'address-city'?: string;
  'address-subdivision'?: string;
  'address-postal-code'?: string;
  'identification-number'?: string;
}

export interface VerificationDatabaseAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  birthdate?: string;
  'address-street-1'?: string;
  'address-street-2'?: string;
  'address-city'?: string;
  'address-subdivision'?: string;
  'address-postal-code'?: string;
  'identification-number'?: string;
  'email-address'?: string;
  'failed-checks'?: VerificationDatabaseDisplayError[];
}

export interface VerificationDatabaseEcbsvAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  birthdate?: string;
  'identification-number'?: string;
  'email-address'?: string;
  relationship?: EcbsvRelationship;
}

export interface VerificationGovernmentIdNfcAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-last'?: string;
  birthdate?: string;
  'identification-number'?: string;
  'issuing-country'?: CountryCode;
  'expiration-date'?: string;
  'portrait-photo-url'?: string;
  sex?: string;
}

export enum EcbsvRelationship {
  Parent = 'parent',
  Guardian = 'guardian',
  Custodian = 'custodian',
}

export interface VerificationDocumentRequestAttributes extends VerificationCoreAttributes {
  'document-id': string;
}

export interface SubmittedAnswer {
  'question-id': number;
  'answer-id': string;
}

export interface Answer {
  id: number;
  answer: string;
}

export interface Question {
  id: number;
  question: string;
  answers: Answer[];
}

interface SelfieFile {
  'capture-method'?: CaptureMethod;
  data?: PreparedFileUpload;
}

export interface VerificationSelfieAttributes extends VerificationCoreAttributes {
  'center-photo'?: SelfieFile;
  'left-photo'?: SelfieFile;
  'right-photo'?: SelfieFile;
  'entity-confidence-reasons-public'?: ConfidenceReasonSelfieVerify[];
  'capture-method'?: CaptureMethod;
  'camera-properties'?: CameraInspectionData;
}

export enum ConfidenceReasonSelfieVerify {
  Generic = 'generic',
  Glare = 'glare',
  Glasses = 'glasses',
}

export interface VerificationRelationships {
  inquiry?: ApiRelationship<ApiModel.Inquiry>;
}

export interface VerificationDocumentRelationships extends VerificationRelationships {
  document?: ApiRelationship<ApiModel.DocumentGeneric>;
}

export type VerificationEmailAddress = ApiResource<
  VerificationEmailAddressAttributes,
  VerificationRelationships,
  ApiModel.VerificationEmailAddress
>;
export type VerificationGovernmentId = ApiResource<
  VerificationGovernmentIdAttributes,
  VerificationRelationships,
  ApiModel.VerificationGovernmentId
>;
export type VerificationPhoneNumber = ApiResource<
  VerificationPhoneNumberAttributes,
  VerificationRelationships,
  ApiModel.VerificationPhoneNumber
>;
export type VerificationSelfie = ApiResource<
  VerificationSelfieAttributes,
  VerificationRelationships,
  ApiModel.VerificationSelfie
>;
export type VerificationAamva = ApiResource<
  VerificationAamvaAttributes,
  VerificationRelationships,
  ApiModel.VerificationAamva
>;
export type VerificationDatabase = ApiResource<
  VerificationDatabaseAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabase
>;
export type VerificationDatabaseEcbsv = ApiResource<
  VerificationDatabaseEcbsvAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseEcbsv
>;
export type VerificationGovernmentIdNfc = ApiResource<
  VerificationGovernmentIdNfcAttributes,
  VerificationDocumentRelationships,
  ApiModel.VerificationGovernmentIdNfc
>;
export type VerificationDocument = ApiResource<
  VerificationDocumentRequestAttributes | VerificationCoreAttributes,
  VerificationDocumentRelationships,
  ApiModel.VerificationDocument
>;

export type Verification =
  | VerificationAamva
  | VerificationDatabase
  | VerificationDatabaseEcbsv
  | VerificationDocument
  | VerificationEmailAddress
  | VerificationGovernmentId
  | VerificationGovernmentIdNfc
  | VerificationPhoneNumber
  | VerificationSelfie;
